import { Component, input, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import {
  LucideAngularModule,
  MapPin,
  Phone,
  Globe,
  Clock,
  Star,
  CircleIcon,
} from 'lucide-angular';
import { ReferentielService } from '../../../../core/services/referentiel.service';
import { Doctor } from '../../../../core/interfaces/doctor.interface';
import { Country } from '../../../../core/interfaces/doctor.interface';
import { ThemeService } from '../../../../core/services/theme.service';

export interface CareNetwork {
  id: string;
  name: string;
  type: 'professional' | 'structure' | 'pharmacy';
  address: string;
  phone: string;
  website?: string;
  rating: number;
  distance: number;
  isPreferred: boolean;
  specialties: string[];
  openingHours: string;
  insuranceCoverage: {
    covered: boolean;
    percentage?: number;
  };
}

@Component({
  selector: 'app-care-networks',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './care-networks.component.html',
  styleUrls: ['./care-networks.component.scss'],
})
export class CareNetworksComponent implements OnInit {
  @Input() networks: CareNetwork[] = [];
  @Input() loading: boolean = false;
  @Input() error: string | null = null;
  @Input() organismExternalId: any;
  selectedCountry: Country | null = null;

  // Propriétés pour la recherche
  searchResults: any[] = []; // Peut contenir Doctor, Pharmacy ou Structure
  searchLoading: boolean = false;
  searchError: string | null = null;
  private searchSubject = new Subject<string>();

  // Stockage des données de searchInsurer (tous les assureurs)
  private specialInsurerData: {
    doctors: any[];
    pharmacies: any[];
    medicalStructures: any[];
  } | null = null;

  readonly MapPin = MapPin;
  readonly Phone = Phone;
  readonly Globe = Globe;
  readonly Clock = Clock;
  readonly Star = Star;
  readonly CircleIcon = CircleIcon;

  selectedFilter: 'professional' | 'structure' | 'pharmacy' = 'professional';
  searchTerm: string = '';

  getTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      professional: 'Professionnel',
      structure: 'Structure',
      pharmacy: 'Pharmacie',
    };
    return labels[type] || type;
  }

  getTypeIcon(type: string): string {
    const icons: Record<string, string> = {
      professional: '👨‍⚕️',
      structure: '🏥',
      pharmacy: '💊',
    };
    return icons[type] || '🏥';
  }

  getStars(rating: number): boolean[] {
    return Array.from({ length: 5 }, (_, i) => i < rating);
  }

  formatDistance(distance: number): string {
    if (distance < 1) {
      return `${Math.round(distance * 1000)}m`;
    }
    return `${distance.toFixed(1)}km`;
  }

  onNetworkClick(item: any): void {
    console.log('Item sélectionné:', item);

    // Navigation selon le type de filtre sélectionné
    switch (this.selectedFilter) {
      case 'structure':
        this.onStructureClick(item);
        break;
      case 'pharmacy':
        this.onPharmacyClick(item);
        break;
      default:
        console.log('Type de réseau non géré:', this.selectedFilter);
    }
  }

  onStructureClick(structure: any): void {
    console.log('Structure sélectionnée:', structure);
    // Navigation vers les détails de la structure
    if (structure.id) {
      this.router.navigate([
        `/${this.selectedCountry?.code}/structures`,
        structure.id,
      ]);
    }
  }

  onPharmacyClick(pharmacy: any): void {
    console.log('Pharmacie sélectionnée:', pharmacy);
    // Navigation vers les détails de la pharmacie
    if (pharmacy.id) {
      this.router.navigate([
        `/${this.selectedCountry?.code}/pharmacies`,
        pharmacy.id,
      ]);
    }
  }

  onDoctorClick(doctor: Doctor): void {
    console.log('Professionnel sélectionné:', doctor);
    // Navigation vers les détails du médecin
    if (doctor.id) {
      this.router.navigate([
        `/${this.selectedCountry?.code}/medecins`,
        doctor.id,
      ]);
    }
  }

  onTakeAppointment(doctor: Doctor): void {
    console.log('Prendre RDV avec:', doctor);
    // Ici vous pouvez ajouter la logique pour prendre rendez-vous
  }

  getSearchPlaceholder(): string {
    const placeholders: Record<string, string> = {
      professional: 'Rechercher un professionnel de santé...',
      structure: 'Rechercher une structure médicale...',
      pharmacy: 'Rechercher une pharmacie...',
    };
    return placeholders[this.selectedFilter] || 'Rechercher...';
  }

  getEmptyMessage(): string {
    const messages: Record<string, string> = {
      professional: 'Aucun professionnel de santé trouvé',
      structure: 'Aucune structure médicale trouvée',
      pharmacy: 'Aucune pharmacie trouvée',
    };
    return messages[this.selectedFilter] || 'Aucun réseau trouvé';
  }

  constructor(
    private referentielService: ReferentielService,
    private router: Router,
    private themeService: ThemeService,
  ) {}

  ngOnInit(): void {
    console.log(
      '🚀 [CARE-NETWORKS] Composant initialisé avec organismExternalId:',
      this.organismExternalId,
    );

    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('defaultCountry');
      if (saved) {
        try {
          this.selectedCountry = JSON.parse(saved);
        } catch {
          this.selectedCountry = null;
        }
      }
    }
    // Charger les données par défaut au démarrage
    this.loadDefaultData();

    // Configuration de la recherche avec debounce
    this.searchSubject
      .pipe(
        debounceTime(300), // Attendre 300ms après la dernière frappe
        distinctUntilChanged(), // Éviter les requêtes identiques
        switchMap((query: string) => {
          if (!query.trim()) {
            // Si la recherche est vide, recharger les données par défaut
            this.loadDefaultData();
            return [];
          }

          this.searchLoading = true;
          this.searchError = null;

          // Utiliser les méthodes spécifiques selon le filtre sélectionné
          if (this.organismExternalId) {
            switch (this.selectedFilter) {
              case 'professional':
                console.log(
                  "🔍 [CARE-NETWORKS] Recherche: Appel de getDoctors pour l'assureur:",
                  this.organismExternalId,
                );
                return this.referentielService.getDoctors({
                  insurerEyoneExternalId: this.organismExternalId,
                  query: query,
                  countryCode: this.selectedCountry?.code || undefined,
                });

              case 'structure':
                console.log(
                  "🔍 [CARE-NETWORKS] Recherche: Appel de getMedicalStructures pour l'assureur:",
                  this.organismExternalId,
                );
                return this.referentielService.getMedicalStructures({
                  insurerEyoneExternalId: this.organismExternalId,
                  query: query,
                  countryCode: this.selectedCountry?.code || undefined,
                });

              case 'pharmacy':
                console.log(
                  "🔍 [CARE-NETWORKS] Recherche: Appel de getPharmacies pour l'assureur:",
                  this.organismExternalId,
                );
                return this.referentielService.getPharmacies({
                  insurerEyoneExternalId: this.organismExternalId,
                  query: query,
                  countryCode: this.selectedCountry?.code || undefined,
                });

              default:
                return [];
            }
          }

          return [];
        }),
      )
      .subscribe({
        next: (response) => {
          this.searchLoading = false;

          // Traiter la réponse selon le filtre sélectionné
          if (response && response.results) {
            switch (this.selectedFilter) {
              case 'professional':
                console.log(
                  '✅ [CARE-NETWORKS] Réponse reçue de getDoctors:',
                  response,
                );
                this.searchResults = response.results.map((doctor: any) => ({
                  id: doctor.id,
                  firstname: doctor.firstname,
                  lastname: doctor.lastname,
                  eyoneExternalId: doctor.eyoneExternalId,
                  title: { name: 'Dr.' },
                  coordonate: {
                    globalAddressField:
                      doctor.coordonate?.globalAddressField ||
                      'Adresse non disponible',
                    fixNumber: doctor.coordonate?.fixNumber,
                    mobileNumber: doctor.coordonate?.mobileNumber,
                  },
                  specialisations: [],
                }));
                break;

              case 'structure':
                console.log(
                  '✅ [CARE-NETWORKS] Réponse reçue de getMedicalStructures:',
                  response,
                );
                this.searchResults = response.results.map((structure: any) => ({
                  id: structure.id,
                  name: structure.name,
                  eyoneExternalId: structure.eyoneExternalId,
                  eyoneUser: structure.eyoneUser,
                  coordonate: {
                    globalAddressField:
                      structure.coordonate?.globalAddressField ||
                      'Adresse non disponible',
                    fixNumber: structure.coordonate?.fixNumber,
                    mobileNumber: structure.coordonate?.mobileNumber,
                  },
                }));
                break;

              case 'pharmacy':
                console.log(
                  '✅ [CARE-NETWORKS] Réponse reçue de getPharmacies:',
                  response,
                );
                this.searchResults = response.results.map((pharmacy: any) => ({
                  id: pharmacy.id,
                  name: pharmacy.name,
                  eyoneExternalId: pharmacy.eyoneExternalId,
                  eyoneUser: pharmacy.eyoneUser,
                  coordonate: {
                    globalAddressField:
                      pharmacy.coordonate?.globalAddressField ||
                      'Adresse non disponible',
                    fixNumber: pharmacy.coordonate?.fixNumber,
                    mobileNumber: pharmacy.coordonate?.mobileNumber,
                  },
                }));
                break;

              default:
                this.searchResults = [];
            }
          } else {
            this.searchResults = [];
          }
        },
        error: (error) => {
          this.searchLoading = false;
          this.searchError = 'Erreur lors de la recherche';
          console.error('Erreur de recherche:', error);
        },
      });
  }

  // Méthode pour charger les données par défaut
  loadDefaultData(): void {
    console.log('🔍 [CARE-NETWORKS] loadDefaultData appelée avec:', {
      organismExternalId: this.organismExternalId,
      selectedFilter: this.selectedFilter,
    });

    this.searchLoading = true;
    this.searchError = null;

    // Utiliser les méthodes spécifiques selon le filtre sélectionné
    if (this.organismExternalId) {
      switch (this.selectedFilter) {
        case 'professional':
          console.log(
            "🔍 [CARE-NETWORKS] Chargement: Appel de getDoctors pour l'assureur:",
            this.organismExternalId,
          );
          this.referentielService
            .getDoctors({
              insurerEyoneExternalId: this.organismExternalId,
              countryCode: this.selectedCountry?.code || undefined,
            })
            .subscribe({
              next: (response) => {
                console.log(
                  '✅ [CARE-NETWORKS] Réponse reçue de getDoctors:',
                  response,
                );
                this.searchLoading = false;
                if (response && response.results) {
                  this.searchResults = response.results.map((doctor: any) => ({
                    id: doctor.id,
                    firstname: doctor.firstname,
                    lastname: doctor.lastname,
                    eyoneExternalId: doctor.eyoneExternalId,
                    title: { name: 'Dr.' },
                    coordonate: {
                      globalAddressField:
                        doctor.coordonate?.globalAddressField ||
                        'Adresse non disponible',
                      fixNumber: doctor.coordonate?.fixNumber,
                      mobileNumber: doctor.coordonate?.mobileNumber,
                    },
                    specialisations: [],
                  }));
                } else {
                  this.searchResults = [];
                }
              },
              error: (error) => {
                console.error(
                  "❌ [CARE-NETWORKS] Erreur lors de l'appel de getDoctors:",
                  error,
                );
                this.searchLoading = false;
                this.searchError = 'Erreur lors du chargement des médecins';
                this.searchResults = [];
              },
            });
          break;

        case 'structure':
          console.log(
            "🔍 [CARE-NETWORKS] Chargement: Appel de getMedicalStructures pour l'assureur:",
            this.organismExternalId,
          );
          this.referentielService
            .getMedicalStructures({
              insurerEyoneExternalId: this.organismExternalId,
              countryCode: this.selectedCountry?.code || undefined,
            })
            .subscribe({
              next: (response) => {
                console.log(
                  '✅ [CARE-NETWORKS] Réponse reçue de getMedicalStructures:',
                  response,
                );
                this.searchLoading = false;
                if (response && response.results) {
                  this.searchResults = response.results.map(
                    (structure: any) => ({
                      id: structure.id,
                      name: structure.name,
                      eyoneExternalId: structure.eyoneExternalId,
                      eyoneUser: structure.eyoneUser,
                      coordonate: {
                        globalAddressField:
                          structure.coordonate?.globalAddressField ||
                          'Adresse non disponible',
                        fixNumber: structure.coordonate?.fixNumber,
                        mobileNumber: structure.coordonate?.mobileNumber,
                      },
                    }),
                  );
                } else {
                  this.searchResults = [];
                }
              },
              error: (error) => {
                console.error(
                  "❌ [CARE-NETWORKS] Erreur lors de l'appel de getMedicalStructures:",
                  error,
                );
                this.searchLoading = false;
                this.searchError =
                  'Erreur lors du chargement des structures médicales';
                this.searchResults = [];
              },
            });
          break;

        case 'pharmacy':
          console.log(
            "🔍 [CARE-NETWORKS] Chargement: Appel de getPharmacies pour l'assureur:",
            this.organismExternalId,
          );
          this.referentielService
            .getPharmacies({
              insurerEyoneExternalId: this.organismExternalId,
              countryCode: this.selectedCountry?.code || undefined,
            })
            .subscribe({
              next: (response) => {
                console.log(
                  '✅ [CARE-NETWORKS] Réponse reçue de getPharmacies:',
                  response,
                );
                this.searchLoading = false;
                if (response && response.results) {
                  this.searchResults = response.results.map(
                    (pharmacy: any) => ({
                      id: pharmacy.id,
                      name: pharmacy.name,
                      eyoneExternalId: pharmacy.eyoneExternalId,
                      eyoneUser: pharmacy.eyoneUser,
                      coordonate: {
                        globalAddressField:
                          pharmacy.coordonate?.globalAddressField ||
                          'Adresse non disponible',
                        fixNumber: pharmacy.coordonate?.fixNumber,
                        mobileNumber: pharmacy.coordonate?.mobileNumber,
                      },
                    }),
                  );
                } else {
                  this.searchResults = [];
                }
              },
              error: (error) => {
                console.error(
                  "❌ [CARE-NETWORKS] Erreur lors de l'appel de getPharmacies:",
                  error,
                );
                this.searchLoading = false;
                this.searchError = 'Erreur lors du chargement des pharmacies';
                this.searchResults = [];
              },
            });
          break;

        default:
          this.searchResults = [];
          this.searchLoading = false;
      }
      return;
    }

    // Si pas d'organismExternalId, vider les résultats
    this.searchResults = [];
    this.searchLoading = false;
  }

  // Méthode pour traiter la réponse de searchInsurer (tous les assureurs)
  private processSpecialInsurerResponse(response: any): void {
    this.specialInsurerData = {
      doctors: response.doctors || [],
      pharmacies: response.pharmacies || [],
      medicalStructures: response.medicalStructures || [],
    };

    // Filtrer les données selon le filtre sélectionné
    switch (this.selectedFilter) {
      case 'professional':
        this.searchResults = this.specialInsurerData.doctors.map(
          (doctor: any) => ({
            id: doctor.id,
            firstname: doctor.firstname,
            lastname: doctor.lastname,
            eyoneExternalId: doctor.eyoneExternalId,
            // Ajouter des propriétés par défaut pour la compatibilité
            title: { name: 'Dr.' },
            coordonate: {
              globalAddressField:
                doctor.coordonate?.globalAddressField ||
                'Adresse non disponible',
              fixNumber: doctor.coordonate?.fixNumber,
              mobileNumber: doctor.coordonate?.mobileNumber,
            },
            specialisations: [],
          }),
        );
        break;
      case 'structure':
        this.searchResults = this.specialInsurerData.medicalStructures.map(
          (structure: any) => ({
            id: structure.id,
            name: structure.name,
            eyoneExternalId: structure.eyoneExternalId,
            eyoneUser: structure.eyoneUser,
            // Ajouter des propriétés par défaut pour la compatibilité
            coordonate: {
              globalAddressField:
                structure.coordonate?.globalAddressField ||
                'Adresse non disponible',
              fixNumber: structure.coordonate?.fixNumber,
              mobileNumber: structure.coordonate?.mobileNumber,
            },
          }),
        );
        break;
      case 'pharmacy':
        this.searchResults = this.specialInsurerData.pharmacies.map(
          (pharmacy: any) => ({
            id: pharmacy.id,
            name: pharmacy.name,
            eyoneExternalId: pharmacy.eyoneExternalId,
            eyoneUser: pharmacy.eyoneUser,
            // Ajouter des propriétés par défaut pour la compatibilité
            coordonate: {
              globalAddressField:
                pharmacy.coordonate?.globalAddressField ||
                'Adresse non disponible',
              fixNumber: pharmacy.coordonate?.fixNumber,
              mobileNumber: pharmacy.coordonate?.mobileNumber,
            },
          }),
        );
        break;
      default:
        this.searchResults = [];
    }
  }

  // Méthode pour filtrer les données de searchInsurer selon la recherche
  private filterSpecialInsurerData(query: string): any[] {
    if (!this.specialInsurerData) return [];

    const searchTerm = query.toLowerCase();
    let results: any[] = [];

    switch (this.selectedFilter) {
      case 'professional':
        results = this.specialInsurerData.doctors
          .filter((doctor: any) => {
            const fullName =
              `${doctor.firstname} ${doctor.lastname}`.toLowerCase();
            return fullName.includes(searchTerm);
          })
          .map((doctor: any) => ({
            id: doctor.id,
            firstname: doctor.firstname,
            lastname: doctor.lastname,
            eyoneExternalId: doctor.eyoneExternalId,
            title: { name: 'Dr.' },
            coordonate: {
              globalAddressField: doctor.coordonate?.globalAddressField,
              fixNumber: doctor.coordonate?.fixNumber,
              mobileNumber: doctor.coordonate?.mobileNumber,
            },
            specialisations: [],
          }));
        break;
      case 'structure':
        results = this.specialInsurerData.medicalStructures
          .filter((structure: any) => {
            return structure.name.toLowerCase().includes(searchTerm);
          })
          .map((structure: any) => ({
            id: structure.id,
            name: structure.name,
            eyoneExternalId: structure.eyoneExternalId,
            coordonate: {
              globalAddressField: structure.coordonate?.globalAddressField,
              fixNumber: structure.coordonate?.fixNumber,
              mobileNumber: structure.coordonate?.mobileNumber,
            },
          }));
        break;
      case 'pharmacy':
        results = this.specialInsurerData.pharmacies
          .filter((pharmacy: any) => {
            return pharmacy.name.toLowerCase().includes(searchTerm);
          })
          .map((pharmacy: any) => ({
            id: pharmacy.id,
            name: pharmacy.name,
            eyoneExternalId: pharmacy.eyoneExternalId,
            coordonate: {
              globalAddressField: pharmacy.coordonate?.globalAddressField,
              fixNumber: pharmacy.coordonate?.fixNumber,
              mobileNumber: pharmacy.coordonate?.mobileNumber,
            },
          }));
        break;
    }

    return results;
  }

  // Méthode pour déclencher la recherche
  onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  // Méthode pour gérer le changement de filtre
  onFilterChange(): void {
    console.log(
      '🔄 [CARE-NETWORKS] Changement de filtre vers:',
      this.selectedFilter,
    );
    this.searchResults = []; // Vider les résultats précédents
    this.searchTerm = ''; // Vider la recherche
    this.loadDefaultData(); // Charger les nouvelles données par défaut
  }

  // Méthode utilitaire pour obtenir le numéro de téléphone à afficher
  getDisplayPhoneNumber(item: any): string | null {
    if (item.coordonate?.fixNumber) {
      return item.coordonate.fixNumber;
    }
    if (item.coordonate?.mobileNumber) {
      return item.coordonate.mobileNumber;
    }
    return null;
  }

  // Méthode utilitaire pour vérifier si un item a des coordonnées de contact
  hasContactInfo(item: any): boolean {
    return !!(item.coordonate?.fixNumber || item.coordonate?.mobileNumber);
  }

  // Nom du produit selon le thème (FA'JMA ou Passeport Médical)
  getProductName(): string {
    return this.themeService.getProductName();
  }
}
